import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft, Building2, User, Phone, Mail, MapPin, Calendar,
    Smartphone, Car, CreditCard, FileText, Clock, Download,
    TrendingUp, CheckCircle2, AlertCircle, Hash
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) { return new Intl.NumberFormat("sq-AL").format(Math.round(n || 0)); }
function fmtDate(iso) { return iso ? iso.substring(0, 10) : "—"; }

const STATUS_CLASS = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    inactive: "bg-slate-100 text-slate-600 border-slate-200",
    debtor: "bg-rose-50 text-rose-700 border-rose-200",
    expired: "bg-rose-50 text-rose-700 border-rose-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    stock: "bg-sky-50 text-sky-700 border-sky-200",
    draft: "bg-slate-100 text-slate-600 border-slate-200",
    sent: "bg-amber-50 text-amber-700 border-amber-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Stat({ icon: Icon, label, value, sub, accent }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
                <Icon size={16} className={accent || "text-slate-400"} />
            </div>
            <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function ClientProfilePage() {
    const { id } = useParams();
    const { t } = useLang();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const canExport = ["admin", "staff"].includes(user?.role);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/clients/${id}/profile`, { withCredentials: true });
                setData(data);
            } catch (e) {
                setError(e.response?.data?.detail || "Klienti nuk u gjet");
            }
            setLoading(false);
        })();
    }, [id]);

    const exportPayments = async () => {
        try {
            const res = await axios.get(`${API}/export/payments.csv?client_id=${id}`, {
                withCredentials: true, responseType: "blob"
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `pagesat-${data.client.name}.csv`);
            document.body.appendChild(link); link.click(); link.remove();
        } catch { alert("Gabim gjatë eksportimit"); }
    };

    const downloadInvoicePdf = async (inv) => {
        try {
            const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { withCredentials: true, responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `fatura-${inv.invoice_number}.pdf`);
            document.body.appendChild(link); link.click(); link.remove();
        } catch { alert("Gabim gjatë gjenerimit të PDF"); }
    };

    if (loading) return <div className="p-6 text-slate-400" data-testid="profile-loading">{t("loading")}</div>;
    if (error) return (
        <div className="p-6">
            <button onClick={() => navigate("/clients")} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#E7212E] mb-4">
                <ArrowLeft size={15} /> Kthehu tek Klientët
            </button>
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded">{error}</div>
        </div>
    );
    if (!data) return null;

    const { client, devices, vehicles, subscriptions, payments, invoices, device_history, monthly_billing, stats } = data;

    // Billing grid: last 24 months
    const grid = [];
    const now = new Date();
    for (let i = 23; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        grid.push({ key, label: d.toLocaleDateString("sq-AL", { month: "short", year: "2-digit" }), amount: monthly_billing[key] || 0 });
    }
    const maxAmount = Math.max(...grid.map(g => g.amount), 1);

    return (
        <div className="p-6" data-testid="client-profile-page">
            {/* Back navigation */}
            <button onClick={() => navigate("/clients")} data-testid="back-to-clients"
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#E7212E] mb-4 transition-colors">
                <ArrowLeft size={15} /> Kthehu tek Klientët
            </button>

            {/* Header card */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-lg bg-[#E7212E]/10 flex items-center justify-center flex-shrink-0">
                            {client.type === "business"
                                ? <Building2 size={26} className="text-[#E7212E]" />
                                : <User size={26} className="text-[#E7212E]" />}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-2xl font-bold text-slate-900" data-testid="client-name">{client.name}</h1>
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${STATUS_CLASS[client.status] || STATUS_CLASS.inactive}`}>
                                    {t(client.status)}
                                </span>
                                <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-slate-100 text-slate-600 border border-slate-200 capitalize">
                                    {client.type === "business" ? t("business") : t("individual")}
                                </span>
                            </div>
                            {client.company && <p className="text-sm text-slate-500 mt-0.5">{client.company}</p>}
                            <div className="flex items-center gap-4 mt-3 text-sm text-slate-600 flex-wrap">
                                {client.nipt && <span className="flex items-center gap-1.5 font-mono"><Hash size={13} /> {client.nipt}</span>}
                                {client.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {client.phone}</span>}
                                {client.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {client.email}</span>}
                                {client.address && <span className="flex items-center gap-1.5"><MapPin size={13} /> {client.address}</span>}
                                <span className="flex items-center gap-1.5 text-slate-500"><Calendar size={13} /> Regjistruar {fmtDate(stats.registered_at)}</span>
                            </div>
                        </div>
                    </div>
                    {canExport && (
                        <button onClick={exportPayments} data-testid="export-client-payments"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> Export Pagesat CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat icon={Smartphone} label="Pajisjet" value={stats.total_devices} sub={`${stats.active_devices} aktive`} accent="text-sky-500" />
                <Stat icon={Car} label="Automjetet" value={stats.total_vehicles} accent="text-indigo-500" />
                <Stat icon={CheckCircle2} label="Abonime Aktive" value={stats.active_subscriptions} sub={stats.expired_subscriptions > 0 ? `${stats.expired_subscriptions} skaduar` : ""} accent="text-emerald-500" />
                <Stat icon={TrendingUp} label="Paguar Gjithsej" value={`${fmt(stats.total_paid)} L`} sub={`${payments.length} pagesa`} accent="text-[#E7212E]" />
            </div>

            {/* Monthly billing grid */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Historia e Pagesave (24 muaj)</h2>
                    <span className="text-xs text-slate-400">Lek / muaj</span>
                </div>
                <div className="grid grid-cols-12 gap-1" data-testid="billing-grid">
                    {grid.map(g => {
                        const pct = (g.amount / maxAmount) * 100;
                        const paid = g.amount > 0;
                        return (
                            <div key={g.key} className="flex flex-col items-center" title={`${g.label}: ${fmt(g.amount)} L`}>
                                <div className="w-full h-20 flex items-end relative">
                                    <div
                                        className={`w-full rounded-sm transition-all ${paid ? "bg-[#E7212E]" : "bg-slate-100"}`}
                                        style={{ height: paid ? `${Math.max(pct, 8)}%` : "4px" }}
                                    />
                                </div>
                                <span className="text-[10px] text-slate-500 mt-1 font-medium uppercase">{g.label.split(" ")[0]}</span>
                                {paid && <span className="text-[9px] text-slate-400 font-mono">{g.amount >= 1000 ? `${(g.amount / 1000).toFixed(0)}k` : g.amount}</span>}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="devices">
                <TabsList className="mb-4">
                    <TabsTrigger value="devices" data-testid="tab-devices"><Smartphone size={13} className="mr-1.5" /> Pajisjet ({devices.length})</TabsTrigger>
                    <TabsTrigger value="subscriptions" data-testid="tab-subs"><CheckCircle2 size={13} className="mr-1.5" /> Abonime ({subscriptions.length})</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard size={13} className="mr-1.5" /> Pagesa ({payments.length})</TabsTrigger>
                    <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText size={13} className="mr-1.5" /> Fatura ({invoices.length})</TabsTrigger>
                    <TabsTrigger value="vehicles" data-testid="tab-vehicles"><Car size={13} className="mr-1.5" /> Automjete ({vehicles.length})</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history"><Clock size={13} className="mr-1.5" /> Historiku ({device_history.length})</TabsTrigger>
                </TabsList>

                {/* DEVICES */}
                <TabsContent value="devices">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table">
                            <thead><tr><th>IMEI</th><th>Modeli</th><th>Statusi</th><th>Regjistruar</th><th>Shënime</th></tr></thead>
                            <tbody>
                                {devices.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center py-8 text-slate-400">Nuk ka pajisje</td></tr>
                                ) : devices.map(d => (
                                    <tr key={d.id}>
                                        <td className="font-mono text-sm font-medium text-slate-900">{d.imei}</td>
                                        <td className="text-sm">{d.model || "—"}</td>
                                        <td><span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${STATUS_CLASS[d.status] || STATUS_CLASS.inactive}`}>{t(d.status)}</span></td>
                                        <td className="text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                                        <td className="text-xs text-slate-500 max-w-xs truncate">{d.notes || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* SUBSCRIPTIONS */}
                <TabsContent value="subscriptions">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table">
                            <thead><tr><th>IMEI</th><th>Plani</th><th>Çmimi</th><th>Fillimi</th><th>Mbarimi</th><th>Statusi</th></tr></thead>
                            <tbody>
                                {subscriptions.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nuk ka abonime</td></tr>
                                ) : subscriptions.map(s => {
                                    const endDate = new Date(s.end_date);
                                    const isExpired = endDate < new Date();
                                    const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
                                    return (
                                        <tr key={s.id}>
                                            <td className="font-mono text-sm">{s.imei}</td>
                                            <td className="text-sm capitalize">{t(s.plan)}</td>
                                            <td className="font-mono font-semibold">{fmt(s.price)} L</td>
                                            <td className="text-xs text-slate-500">{fmtDate(s.start_date)}</td>
                                            <td className={`text-xs font-medium ${isExpired ? "text-rose-600" : daysLeft <= 15 ? "text-amber-600" : "text-slate-500"}`}>
                                                {fmtDate(s.end_date)}
                                                {s.status === "active" && !isExpired && daysLeft <= 30 && <span className="ml-1 text-[10px]">({daysLeft}d)</span>}
                                                {isExpired && <span className="ml-1 text-[10px]">({Math.abs(daysLeft)}d vonesë)</span>}
                                            </td>
                                            <td><span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${STATUS_CLASS[s.status] || STATUS_CLASS.inactive}`}>{t(s.status)}</span></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* PAYMENTS */}
                <TabsContent value="payments">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table">
                            <thead><tr><th>Data</th><th>IMEI</th><th>Shuma</th><th>Metoda</th><th>Periudha</th><th>Shënime</th></tr></thead>
                            <tbody>
                                {payments.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nuk ka pagesa</td></tr>
                                ) : payments.map(p => (
                                    <tr key={p.id}>
                                        <td className="text-sm text-slate-600">{fmtDate(p.payment_date)}</td>
                                        <td className="font-mono text-xs">{p.imei || "—"}</td>
                                        <td className="font-mono font-semibold text-emerald-600">{fmt(p.amount)} Lek</td>
                                        <td className="text-sm capitalize">{t(p.method) || p.method}</td>
                                        <td className="text-xs text-slate-500">{p.period_label || "—"}</td>
                                        <td className="text-xs text-slate-500 max-w-xs truncate">{p.notes || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* INVOICES */}
                <TabsContent value="invoices">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table">
                            <thead><tr><th>Nr. Faturës</th><th>Data</th><th>Afati</th><th>Totali</th><th>Statusi</th><th></th></tr></thead>
                            <tbody>
                                {invoices.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nuk ka fatura</td></tr>
                                ) : invoices.map(inv => (
                                    <tr key={inv.id}>
                                        <td className="font-mono text-sm font-medium text-[#E7212E]">{inv.invoice_number}</td>
                                        <td className="text-xs text-slate-500">{fmtDate(inv.issue_date)}</td>
                                        <td className="text-xs text-slate-500">{fmtDate(inv.due_date)}</td>
                                        <td className="font-mono font-semibold">{fmt(inv.total)} Lek</td>
                                        <td><span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${STATUS_CLASS[inv.status] || STATUS_CLASS.draft}`}>{inv.status}</span></td>
                                        <td>
                                            <button onClick={() => downloadInvoicePdf(inv)}
                                                className="flex items-center gap-1 px-2.5 py-1 rounded bg-slate-100 hover:bg-[#E7212E] hover:text-white text-slate-700 text-xs font-medium transition-colors">
                                                <Download size={12} /> PDF
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* VEHICLES */}
                <TabsContent value="vehicles">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table">
                            <thead><tr><th>Targa</th><th>Modeli</th><th>Shoferi</th><th>IMEI</th></tr></thead>
                            <tbody>
                                {vehicles.length === 0 ? (
                                    <tr><td colSpan={4} className="text-center py-8 text-slate-400">Nuk ka automjete</td></tr>
                                ) : vehicles.map(v => (
                                    <tr key={v.id}>
                                        <td className="font-mono font-semibold">{v.plate}</td>
                                        <td className="text-sm">{v.model || "—"}</td>
                                        <td className="text-sm">{v.driver || "—"}</td>
                                        <td className="font-mono text-xs text-slate-500">{v.imei || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* HISTORY */}
                <TabsContent value="history">
                    <div className="bg-white border border-slate-200 rounded p-4">
                        {device_history.length === 0 ? (
                            <div className="text-center py-8 text-slate-400">Nuk ka histori</div>
                        ) : (
                            <div className="space-y-3">
                                {device_history.map(h => (
                                    <div key={h._id || h.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${h.action === "transferred" ? "bg-amber-100 text-amber-600" : h.action === "unassigned" ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
                                            <AlertCircle size={14} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-slate-900 capitalize">
                                                {t(h.action) || h.action} — <span className="font-mono text-xs text-slate-600">{h.imei}</span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {h.from_client_name && <span>Nga: {h.from_client_name}</span>}
                                                {h.from_client_name && h.to_client_name && <span> → </span>}
                                                {h.to_client_name && <span>Tek: {h.to_client_name}</span>}
                                                {!h.from_client_name && !h.to_client_name && <span>—</span>}
                                            </div>
                                            {h.notes && <div className="text-xs text-slate-500 italic mt-0.5">{h.notes}</div>}
                                        </div>
                                        <span className="text-xs text-slate-400">{fmtDate(h.date)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
